import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
} from "@mui/material";
import { green, grey } from "@mui/material/colors";
import axios from "axios";
import { createChatSocket } from "../../utils/chatSocket";
import CloseIcon from "@mui/icons-material/Close";
import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";

const socket = createChatSocket();

const headerStyle = {
  fontWeight: "bold",
  backgroundColor: green[700],
  color: "white",
};

export default function RdvAttendance(props) {
  const { open, onClose, dsid, rdvId, rname, apiAddr } = props;
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checkInType, setCheckInType] = useState("fire");
  const [insaCode, setInsaCode] = useState("");
  const [pname, setPname] = useState("");
  const [porg, setPorg] = useState("");
  const [pdept, setPdept] = useState("");
  const [prank, setPrank] = useState("");
  const [pphone, setPphone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [checkoutSubmitting, setCheckoutSubmitting] = useState(null);
  const [selectedForCheckout, setSelectedForCheckout] = useState(null);

  const fetchList = async () => {
    if (!apiAddr || !dsid || rdvId == null) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${apiAddr}/get/rdvattendance?dsid=${encodeURIComponent(dsid)}&rdv_id=${rdvId}`
      );
      setList(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.warn("rdvattendance fetch failed", e);
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && dsid && rdvId != null) fetchList();
    else setList([]);
    if (!open) setSelectedForCheckout(null);
  }, [open, dsid, rdvId, apiAddr]);

  const handleCheckIn = async () => {
    if (!apiAddr || !dsid || rdvId == null) return;
    setSubmitting(true);
    try {
      const body =
        checkInType === "fire"
          ? {
              dsid,
              rdv_id: rdvId,
              person_type: "fire",
              pname: pname.trim(),
              porg: porg.trim(),
              pdept: pdept.trim(),
              prank: prank.trim(),
              insa_code: insaCode.trim(),
              pphone: pphone.trim(),
            }
          : {
              dsid,
              rdv_id: rdvId,
              person_type: "other",
              pname: pname.trim(),
              porg: porg.trim(),
              pdept: pdept.trim(),
              prank: prank.trim(),
              pphone: pphone.trim(),
            };
      const res = await axios.post(`${apiAddr}/post/rdvcheckin`, body);
      if (res.data && res.data.result === "success") {
        fetchList();
        socket.emit("rdvattendance", { dsid });
        setPname("");
        setPorg("");
        setPdept("");
        setPrank("");
        setPphone("");
        setInsaCode("");
      } else {
        alert(res.data?.message || "출근 처리에 실패했습니다.");
      }
    } catch (e) {
      const msg = e.response?.data?.message || "출근 처리 중 오류가 발생했습니다.";
      alert(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCheckOut = async (snum) => {
    const targetSnum = snum ?? selectedForCheckout;
    if (!apiAddr || targetSnum == null) return;
    setCheckoutSubmitting(targetSnum);
    try {
      const res = await axios.post(`${apiAddr}/post/rdvcheckout`, { snum: targetSnum });
      if (res.data && res.data.result === "success") {
        fetchList();
        setSelectedForCheckout(null);
        socket.emit("rdvattendance", { dsid });
      }
      else alert(res.data?.message || "퇴근 처리에 실패했습니다.");
    } catch (e) {
      alert(e.response?.data?.message || "퇴근 처리 중 오류가 발생했습니다.");
    } finally {
      setCheckoutSubmitting(null);
    }
  };

  const displayName = (row) => {
    if (row.person_type === "fire") return row.insa_name || row.pname || row.insa_code || "-";
    return row.pname || "-";
  };

  const displayOrg = (row) => row.insa_org || row.porg || "-";
  const displayDept = (row) => row.pdept || "-";
  const displayRank = (row) => row.prank || row.insa_rank || "-";

  if (!open) return null;

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "95%",
          maxWidth: 448,
          maxHeight: "72vh",
          bgcolor: "background.paper",
          borderRadius: 1,
          boxShadow: 24,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box sx={{ ...headerStyle, display: "flex", alignItems: "center", justifyContent: "center", position: "relative", px: 1.5, py: 1.5, minHeight: 44 }}>
          <Typography sx={{ fontWeight: "bold", fontSize: "1.2rem", textAlign: "center" }}>
            지원인력 출퇴근 관리{rname ? ` - ${rname}` : ""}
          </Typography>
          <Button
            size="small"
            onClick={onClose}
            sx={{ position: "absolute", right: 8, color: "white", minWidth: 0 }}
          >
            <CloseIcon fontSize="small" />
          </Button>
        </Box>

        <Box sx={{ p: 1.5, overflowY: "auto", flex: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            출근 체크
          </Typography>
          <ToggleButtonGroup
            value={checkInType}
            exclusive
            onChange={(e, v) => v != null && setCheckInType(v)}
            size="small"
            sx={{ mb: 1 }}
          >
            <ToggleButton value="fire">소방공무원</ToggleButton>
            <ToggleButton value="other">그 외</ToggleButton>
          </ToggleButtonGroup>

          {checkInType === "fire" ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 1.5 }}>
              <TextField size="small" label="기관" value={porg} onChange={(e) => setPorg(e.target.value)} sx={{ maxWidth: 220 }} />
              <TextField size="small" label="부서" value={pdept} onChange={(e) => setPdept(e.target.value)} sx={{ maxWidth: 220 }} />
              <TextField size="small" label="계급" value={prank} onChange={(e) => setPrank(e.target.value)} sx={{ maxWidth: 220 }} />
              <TextField size="small" label="성명 *" value={pname} onChange={(e) => setPname(e.target.value)} required sx={{ maxWidth: 220 }} />
              <TextField size="small" label="후대번호(인사코드)" value={insaCode} onChange={(e) => setInsaCode(e.target.value)} placeholder="선택" sx={{ maxWidth: 220 }} />
              <TextField size="small" label="연락처" value={pphone} onChange={(e) => setPphone(e.target.value)} sx={{ maxWidth: 220 }} />
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 1.5 }}>
              <TextField size="small" label="기관" value={porg} onChange={(e) => setPorg(e.target.value)} sx={{ maxWidth: 220 }} />
              <TextField size="small" label="부서" value={pdept} onChange={(e) => setPdept(e.target.value)} sx={{ maxWidth: 220 }} />
              <TextField size="small" label="계급" value={prank} onChange={(e) => setPrank(e.target.value)} sx={{ maxWidth: 220 }} />
              <TextField size="small" label="성명 *" value={pname} onChange={(e) => setPname(e.target.value)} required sx={{ maxWidth: 220 }} />
              <TextField size="small" label="연락처" value={pphone} onChange={(e) => setPphone(e.target.value)} sx={{ maxWidth: 220 }} />
            </Box>
          )}

          <Box sx={{ display: "flex", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              size="medium"
              startIcon={<LoginIcon />}
              onClick={handleCheckIn}
              disabled={!pname.trim() || submitting}
              sx={{ bgcolor: green[600], "&:hover": { bgcolor: green[700] } }}
            >
              출근확인
            </Button>
            <Button
              variant="outlined"
              size="medium"
              startIcon={<LogoutIcon />}
              onClick={() => handleCheckOut()}
              disabled={!selectedForCheckout || !!list.find((r) => r.snum === selectedForCheckout)?.check_out_dt || checkoutSubmitting}
              sx={{ borderColor: green[600], color: green[700], "&:hover": { borderColor: green[700], bgcolor: green[50] } }}
            >
              퇴근확인
            </Button>
          </Box>

          <Divider sx={{ my: 1.5 }} />
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
            출퇴근 목록
          </Typography>
          <TableContainer component={Paper} sx={{ maxHeight: 280 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={headerStyle}>구분</TableCell>
                  <TableCell sx={headerStyle}>성명</TableCell>
                  <TableCell sx={headerStyle}>기관</TableCell>
                  <TableCell sx={headerStyle}>부서</TableCell>
                  <TableCell sx={headerStyle}>계급</TableCell>
                  <TableCell sx={headerStyle}>연락처</TableCell>
                  <TableCell sx={headerStyle}>출근시각</TableCell>
                  <TableCell sx={headerStyle}>퇴근시각</TableCell>
                  <TableCell sx={headerStyle} align="center">
                    퇴근
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      조회 중...
                    </TableCell>
                  </TableRow>
                ) : list.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ color: grey[600] }}>
                      출근 기록이 없습니다.
                    </TableCell>
                  </TableRow>
                ) : (
                  list.map((row) => {
                    const isSelected = selectedForCheckout === row.snum;
                    const canSelect = !row.check_out_dt;
                    return (
                      <TableRow
                        key={row.snum}
                        hover
                        selected={isSelected}
                        onClick={() => canSelect && setSelectedForCheckout(isSelected ? null : row.snum)}
                        sx={{ cursor: canSelect ? "pointer" : "default" }}
                      >
                        <TableCell>{row.person_type === "fire" ? "소방공무원" : "그 외"}</TableCell>
                        <TableCell>{displayName(row)}</TableCell>
                      <TableCell>{displayOrg(row)}</TableCell>
                      <TableCell>{displayDept(row)}</TableCell>
                      <TableCell>{displayRank(row)}</TableCell>
                      <TableCell>{row.pphone || "-"}</TableCell>
                        <TableCell>{row.check_in_dt || "-"}</TableCell>
                        <TableCell>{row.check_out_dt || "-"}</TableCell>
                        <TableCell align="center">
                          {row.check_out_dt ? (
                            <Typography variant="caption" color="text.secondary">
                              완료
                            </Typography>
                          ) : (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<LogoutIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCheckOut(row.snum);
                              }}
                              disabled={checkoutSubmitting === row.snum}
                              sx={{ fontSize: "0.7rem" }}
                            >
                              퇴근
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </Modal>
  );
}
